/**
 * Demo Board Data
 *
 * Sample board data for testing the board view components.
 * Will be replaced with API data in production.
 */

import type { ColumnType } from '@wmp/shared/constants';

// Types for board data structure
export interface Column {
  id: string;
  name: string;
  type: ColumnType;
  width: number;
  position: number;
}

export interface Item {
  id: string;
  name: string;
  position: number;
  values: Record<string, unknown>;
  subitemCount?: number;
  updateCount?: number;
  fileCount?: number;
}

export interface Group {
  id: string;
  name: string;
  color: string;
  position: number;
  items: Item[];
}

export interface Board {
  id: string;
  name: string;
  description?: string;
  isFavorite?: boolean;
  memberCount?: number;
  columns: Column[];
  groups: Group[];
}

/**
 * Demo board for testing - will be replaced with API data
 */
export const DEMO_BOARD: Board = {
  id: 'demo-board',
  name: 'Sprint Planning',
  description: 'Track and manage current sprint tasks',
  isFavorite: true,
  memberCount: 5,
  columns: [
    { id: 'col-status', name: 'Status', type: 'status', width: 130, position: 0 },
    { id: 'col-owner', name: 'Owner', type: 'people', width: 120, position: 1 },
    { id: 'col-date', name: 'Due Date', type: 'date', width: 120, position: 2 },
    { id: 'col-priority', name: 'Priority', type: 'priority', width: 100, position: 3 },
    { id: 'col-effort', name: 'Effort', type: 'numbers', width: 80, position: 4 },
  ],
  groups: [
    {
      id: 'group-1',
      name: 'To Do',
      color: '#579bfc',
      position: 0,
      items: [
        {
          id: 'item-1',
          name: 'Set up authentication flow',
          position: 0,
          values: {
            'col-status': { labelId: 'pending', label: 'Pending', color: '#c4c4c4' },
            'col-owner': { personIds: ['user-1'] },
            'col-date': { date: '2026-02-05' },
            'col-priority': { priority: 'high' },
            'col-effort': { value: 5 },
          },
          updateCount: 2,
        },
        {
          id: 'item-2',
          name: 'Design database schema',
          position: 1,
          values: {
            'col-status': { labelId: 'planning', label: 'Planning', color: '#ff5ac4' },
            'col-owner': { personIds: ['user-2', 'user-3'] },
            'col-date': { date: '2026-02-03' },
            'col-priority': { priority: 'medium' },
            'col-effort': { value: 8 },
          },
          subitemCount: 3,
        },
      ],
    },
    {
      id: 'group-2',
      name: 'In Progress',
      color: '#fdab3d',
      position: 1,
      items: [
        {
          id: 'item-3',
          name: 'Implement board view components',
          position: 0,
          values: {
            'col-status': { labelId: 'working', label: 'Working on it', color: '#fdab3d' },
            'col-owner': { personIds: ['user-1'] },
            'col-date': { date: '2026-02-02' },
            'col-priority': { priority: 'critical' },
            'col-effort': { value: 13 },
          },
          updateCount: 5,
          fileCount: 2,
        },
      ],
    },
    {
      id: 'group-3',
      name: 'Done',
      color: '#00c875',
      position: 2,
      items: [
        {
          id: 'item-4',
          name: 'Project setup and configuration',
          position: 0,
          values: {
            'col-status': { labelId: 'done', label: 'Done', color: '#00c875' },
            'col-owner': { personIds: ['user-1', 'user-2'] },
            'col-date': { date: '2026-01-28' },
            'col-priority': { priority: 'high' },
            'col-effort': { value: 3 },
          },
        },
      ],
    },
  ],
};
